import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
    ReactFlow,
    Background,
    Controls,
    MiniMap,
    useNodesState,
    useEdgesState,
    addEdge,
    MarkerType,
    Handle,
    Position
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { productsApi, productWorkflowsApi, stepLibraryApi, rolesApi } from '../lib/api';
import { ROLE_LABELS } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import {
    ArrowLeft,
    Plus,
    FloppyDisk,
    Trash,
    Gear,
    Cube,
    ArrowRight,
    Warning,
    Check,
    Clock,
    Folder,
    X,
    Copy,
    TextAlignLeft,
    FileText,
    ListChecks
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import StepFormBuilder from '../components/StepFormBuilder';
import StepDocumentBuilder from '../components/StepDocumentBuilder';

// Constants for ReactFlow configuration
const SNAP_GRID = [15, 15];

// Custom Node Component
const WorkflowNode = ({ data, selected }) => {
    const config = data.effective_config || data.step_snapshot || data;
    const hasFormOverride = data.overrides?.form_sections?.length > 0;
    const hasFormConfig = config.form_sections?.length > 0 || hasFormOverride;
    
    return (
        <div 
            className={`px-4 py-3 bg-white border-2 rounded-lg shadow-sm min-w-[200px] max-w-[250px] transition-all ${
                selected ? 'border-accent shadow-lg ring-2 ring-accent/20' : 'border-border'
            }`}
        >
            <Handle 
                type="target" 
                position={Position.Top} 
                className="!w-3 !h-3 !bg-primary !border-2 !border-white"
            />
            
            <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{config.name || data.label}</p>
                    <p className="text-xs text-muted-foreground font-mono">{config.code || data.code}</p>
                </div>
                {data.step_library_id && (
                    <Cube className="w-4 h-4 text-primary flex-shrink-0 ml-2" />
                )}
            </div>
            
            <div className="flex flex-wrap gap-1 mt-2">
                {config.is_blocking && (
                    <Badge variant="outline" className="text-xs py-0 px-1">
                        <Warning className="w-2.5 h-2.5 mr-0.5" />Bloccante
                    </Badge>
                )}
                {config.approval_required && (
                    <Badge variant="outline" className="text-xs py-0 px-1 border-amber-300 text-amber-700">
                        <Check className="w-2.5 h-2.5 mr-0.5" />Approv.
                    </Badge>
                )}
                {config.sla_days && (
                    <Badge variant="outline" className="text-xs py-0 px-1">
                        <Clock className="w-2.5 h-2.5 mr-0.5" />{config.sla_days}g
                    </Badge>
                )}
                {hasFormConfig && (
                    <Badge variant="outline" className={`text-xs py-0 px-1 ${hasFormOverride ? 'border-blue-400 text-blue-700 bg-blue-50' : ''}`}>
                        <TextAlignLeft className="w-2.5 h-2.5 mr-0.5" />Form
                    </Badge>
                )}
            </div>
            
            {config.assigned_role && (
                <p className="text-xs text-muted-foreground mt-2 truncate">
                    {ROLE_LABELS[config.assigned_role] || config.assigned_role}
                </p>
            )}
            
            <Handle 
                type="source" 
                position={Position.Bottom} 
                className="!w-3 !h-3 !bg-primary !border-2 !border-white"
            />
        </div>
    );
};

const nodeTypes = { workflowNode: WorkflowNode };

const defaultEdgeOptions = {
    type: 'smoothstep',
    markerEnd: { type: MarkerType.ArrowClosed, color: 'hsl(var(--primary))' },
    style: { stroke: 'hsl(var(--border))', strokeWidth: 2 },
    animated: false
};

export default function ProductWorkflowBuilderPage() {
    const { productId, workflowId } = useParams();
    const navigate = useNavigate();
    const reactFlowWrapper = useRef(null);
    
    const [product, setProduct] = useState(null);
    const [workflow, setWorkflow] = useState(null);
    const [stepLibrary, setStepLibrary] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const [selectedNode, setSelectedNode] = useState(null);
    
    const [workflowInfo, setWorkflowInfo] = useState({
        name: '',
        description: '',
        is_default: false
    });
    
    const [stepLibraryDialogOpen, setStepLibraryDialogOpen] = useState(false);
    const [nodeEditorDialogOpen, setNodeEditorDialogOpen] = useState(false);
    const [editingNodeData, setEditingNodeData] = useState(null);
    const [stepFilter, setStepFilter] = useState('');
    const [stepCategoryFilter, setStepCategoryFilter] = useState('all');

    // Load data
    const loadData = useCallback(async () => {
        try {
            const [productRes, rolesRes, stepsRes] = await Promise.all([
                productsApi.getOne(productId),
                rolesApi.getAll(),
                stepLibraryApi.getAll({ active_only: true })
            ]);
            
            setProduct(productRes.data);
            setRoles(rolesRes.data);
            setStepLibrary(stepsRes.data);
            
            // Load workflow if editing
            if (workflowId && workflowId !== 'new') {
                const workflowRes = await productWorkflowsApi.getOne(workflowId);
                const wf = workflowRes.data;
                setWorkflow(wf);
                setWorkflowInfo({
                    name: wf.name || '',
                    description: wf.description || '',
                    is_default: wf.is_default || false
                });
                
                // Convert workflow nodes to React Flow nodes
                const flowNodes = (wf.nodes || []).map((node, idx) => ({
                    id: node.node_id || String(idx + 1),
                    type: 'workflowNode',
                    position: node.position || { x: 100, y: 100 + idx * 150 },
                    data: {
                        ...node,
                        label: node.step_snapshot?.name || node.effective_config?.name || `Step ${idx + 1}`,
                        code: node.step_snapshot?.code || node.effective_config?.code || `STEP_${idx + 1}`
                    }
                }));
                
                // Convert workflow edges to React Flow edges
                const flowEdges = (wf.edges || []).map(edge => ({
                    id: edge.edge_id || `e-${edge.source_node_id}-${edge.target_node_id}`,
                    source: edge.source_node_id,
                    target: edge.target_node_id,
                    ...defaultEdgeOptions
                }));
                
                setNodes(flowNodes);
                setEdges(flowEdges);
            } else {
                // New workflow
                setWorkflowInfo({
                    name: `Workflow ${productRes.data.name}`,
                    description: '',
                    is_default: productRes.data.workflow_count === 0
                });
            }
        } catch (error) {
            console.error('Error loading data:', error);
            toast.error('Errore nel caricamento');
        } finally {
            setLoading(false);
        }
    }, [productId, workflowId, setNodes, setEdges]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // Handle edge connection
    const onConnect = useCallback(
        (params) => {
            // Prevent duplicate edges
            const exists = edges.some(
                e => e.source === params.source && e.target === params.target
            );
            if (exists) return;
            
            setEdges((eds) => addEdge({
                ...params,
                ...defaultEdgeOptions,
                id: `e-${params.source}-${params.target}`
            }, eds));
        },
        [edges, setEdges]
    );

    // Handle node click
    const onNodeClick = useCallback((event, node) => {
        setSelectedNode(node);
    }, []);

    // Handle pane click (deselect)
    const onPaneClick = useCallback(() => {
        setSelectedNode(null);
    }, []);

    // Add step from library
    const addStepFromLibrary = (step) => {
        const nodeId = `node_${Date.now()}`;
        const newNode = {
            id: nodeId,
            type: 'workflowNode',
            position: { 
                x: 100 + (nodes.length % 3) * 280, 
                y: 100 + Math.floor(nodes.length / 3) * 180 
            },
            data: {
                step_library_id: step.id,
                node_id: nodeId,
                order: nodes.length,
                step_snapshot: {
                    code: step.code,
                    name: step.name,
                    description: step.description,
                    assigned_role: step.assigned_role,
                    sla_days: step.sla_days,
                    is_blocking: step.is_blocking,
                    approval_required: step.approval_required,
                    required_documents: step.required_documents || [],
                    optional_documents: step.optional_documents || [],
                    checklist: step.checklist || [],
                    form_sections: step.form_sections || []  // Include form from step library
                },
                effective_config: {
                    code: step.code,
                    name: step.name,
                    description: step.description,
                    assigned_role: step.assigned_role,
                    sla_days: step.sla_days,
                    is_blocking: step.is_blocking,
                    approval_required: step.approval_required,
                    required_documents: step.required_documents || [],
                    optional_documents: step.optional_documents || [],
                    checklist: step.checklist || [],
                    form_sections: step.form_sections || []
                },
                overrides: {},
                label: step.name,
                code: step.code
            }
        };
        
        setNodes((nds) => [...nds, newNode]);
        setStepLibraryDialogOpen(false);
        toast.success(`Step "${step.name}" aggiunto`);
    };

    // Add custom node (not from library)
    const addCustomNode = () => {
        const nodeId = `node_${Date.now()}`;
        const nodeNum = nodes.length + 1;
        const newNode = {
            id: nodeId,
            type: 'workflowNode',
            position: { 
                x: 100 + (nodes.length % 3) * 280, 
                y: 100 + Math.floor(nodes.length / 3) * 180 
            },
            data: {
                step_library_id: null,
                node_id: nodeId,
                order: nodes.length,
                step_snapshot: {
                    code: `CUSTOM_STEP_${nodeNum}`,
                    name: `Step Personalizzato ${nodeNum}`,
                    description: '',
                    assigned_role: '',
                    sla_days: 7,
                    is_blocking: true,
                    approval_required: false,
                    required_documents: [],
                    optional_documents: [],
                    checklist: [],
                    form_sections: []  // Empty form for custom steps
                },
                effective_config: {
                    code: `CUSTOM_STEP_${nodeNum}`,
                    name: `Step Personalizzato ${nodeNum}`,
                    description: '',
                    assigned_role: '',
                    sla_days: 7,
                    is_blocking: true,
                    approval_required: false,
                    required_documents: [],
                    optional_documents: [],
                    checklist: [],
                    form_sections: []
                },
                overrides: {},
                label: `Step Personalizzato ${nodeNum}`,
                code: `CUSTOM_STEP_${nodeNum}`
            }
        };
        
        setNodes((nds) => [...nds, newNode]);
    };

    // Open node editor
    const openNodeEditor = () => {
        if (!selectedNode) return;
        setEditingNodeData({
            ...selectedNode.data,
            id: selectedNode.id
        });
        setNodeEditorDialogOpen(true);
    };

    // Save node changes
    const saveNodeChanges = () => {
        if (!editingNodeData) return;
        
        setNodes((nds) =>
            nds.map((n) => {
                if (n.id === editingNodeData.id) {
                    // Merge overrides with snapshot to get effective config
                    const baseConfig = editingNodeData.step_snapshot || {};
                    const overrides = editingNodeData.overrides || {};
                    
                    // Build effective config with override priority
                    const effectiveConfig = { ...baseConfig };
                    Object.keys(overrides).forEach(key => {
                        if (overrides[key] !== undefined && overrides[key] !== null) {
                            // For form_sections, only use override if it has content
                            if (key === 'form_sections') {
                                if (Array.isArray(overrides[key]) && overrides[key].length > 0) {
                                    effectiveConfig[key] = overrides[key];
                                }
                            } else {
                                effectiveConfig[key] = overrides[key];
                            }
                        }
                    });
                    
                    return {
                        ...n,
                        data: {
                            ...editingNodeData,
                            effective_config: effectiveConfig,
                            label: effectiveConfig.name || editingNodeData.label,
                            code: effectiveConfig.code || editingNodeData.code
                        }
                    };
                }
                return n;
            })
        );
        
        setSelectedNode(null);
        setNodeEditorDialogOpen(false);
        setEditingNodeData(null);
        toast.success('Configurazione step salvata');
    };

    // Update override
    const updateOverride = (key, value) => {
        setEditingNodeData(prev => ({
            ...prev,
            overrides: {
                ...(prev.overrides || {}),
                [key]: value
            }
        }));
    };

    // Delete selected node
    const deleteSelectedNode = () => {
        if (!selectedNode) return;
        setNodes((nds) => nds.filter((n) => n.id !== selectedNode.id));
        setEdges((eds) => eds.filter((e) => e.source !== selectedNode.id && e.target !== selectedNode.id));
        setSelectedNode(null);
    };

    // Validate workflow before save
    const validateWorkflow = () => {
        const warnings = [];
        const errors = [];
        
        // Check if workflow has at least one node
        if (nodes.length === 0) {
            errors.push('Il workflow deve contenere almeno uno step');
        }
        
        // Check for disconnected nodes
        if (nodes.length > 1) {
            const connectedNodes = new Set();
            edges.forEach(e => {
                connectedNodes.add(e.source);
                connectedNodes.add(e.target);
            });
            
            const disconnectedNodes = nodes.filter(n => !connectedNodes.has(n.id));
            if (disconnectedNodes.length > 0) {
                warnings.push(`${disconnectedNodes.length} nodi non collegati: ${disconnectedNodes.map(n => n.data.step_snapshot?.name || n.id).join(', ')}`);
            }
        }
        
        // Check for start node (no incoming edges)
        if (nodes.length > 0 && edges.length > 0) {
            const hasIncoming = new Set(edges.map(e => e.target));
            const startNodes = nodes.filter(n => !hasIncoming.has(n.id));
            if (startNodes.length === 0) {
                warnings.push('Nessuno step iniziale rilevato (tutti hanno collegamenti in entrata)');
            } else if (startNodes.length > 1) {
                warnings.push(`${startNodes.length} possibili step iniziali: ${startNodes.map(n => n.data.step_snapshot?.name || n.id).join(', ')}`);
            }
        }
        
        return { warnings, errors };
    };

    // Save workflow
    const saveWorkflow = async () => {
        if (!workflowInfo.name) {
            toast.error('Il nome del workflow è obbligatorio');
            return;
        }
        
        // Validate before save
        const { warnings, errors } = validateWorkflow();
        
        if (errors.length > 0) {
            errors.forEach(err => toast.error(err));
            return;
        }
        
        if (warnings.length > 0) {
            warnings.forEach(warn => toast.warning(warn, { duration: 5000 }));
        }
        
        setSaving(true);
        try {
            const workflowData = {
                product_id: productId,
                name: workflowInfo.name,
                description: workflowInfo.description,
                is_default: workflowInfo.is_default,
                nodes: nodes.map((n, idx) => ({
                    step_library_id: n.data.step_library_id,
                    node_id: n.id,
                    order: idx,
                    position: n.position,
                    step_snapshot: n.data.step_snapshot,
                    overrides: n.data.overrides || {}
                })),
                edges: edges.map(e => ({
                    edge_id: e.id,
                    source_node_id: e.source,
                    target_node_id: e.target
                }))
            };
            
            if (workflowId && workflowId !== 'new') {
                await productWorkflowsApi.update(workflowId, workflowData);
                toast.success('Workflow aggiornato');
            } else {
                const response = await productWorkflowsApi.create(workflowData);
                toast.success('Workflow creato');
                navigate(`/workflow-console/product/${productId}/workflow/${response.data.id}`, { replace: true });
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
        } finally {
            setSaving(false);
        }
    };

    // Filter steps in library dialog
    const filteredSteps = useMemo(() => {
        return stepLibrary.filter(s => {
            const matchesSearch = !stepFilter || 
                s.name?.toLowerCase().includes(stepFilter.toLowerCase()) ||
                s.code?.toLowerCase().includes(stepFilter.toLowerCase());
            const matchesCategory = stepCategoryFilter === 'all' || s.category === stepCategoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [stepLibrary, stepFilter, stepCategoryFilter]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-8rem)] flex flex-col" data-testid="product-workflow-builder-page">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                    <Link
                        to={`/workflow-console/product/${productId}`}
                        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        {product?.name}
                    </Link>
                    <div className="h-6 w-px bg-border" />
                    <Input
                        value={workflowInfo.name}
                        onChange={(e) => setWorkflowInfo({ ...workflowInfo, name: e.target.value })}
                        placeholder="Nome workflow"
                        className="font-heading text-lg font-semibold h-8 border-0 p-0 focus-visible:ring-0 w-64"
                        data-testid="workflow-name-input"
                    />
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="is_default"
                            checked={workflowInfo.is_default}
                            onCheckedChange={(checked) => setWorkflowInfo({ ...workflowInfo, is_default: checked })}
                        />
                        <Label htmlFor="is_default" className="text-sm cursor-pointer">Default</Label>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setStepLibraryDialogOpen(true)} data-testid="add-from-library-button">
                        <Cube className="w-4 h-4 mr-2" />
                        Da Libreria
                    </Button>
                    <Button variant="outline" onClick={addCustomNode} data-testid="add-custom-node-button">
                        <Plus className="w-4 h-4 mr-2" />
                        Step Custom
                    </Button>
                    <Button onClick={saveWorkflow} disabled={saving} data-testid="save-workflow-button">
                        <FloppyDisk className="w-4 h-4 mr-2" />
                        {saving ? 'Salvataggio...' : 'Salva'}
                    </Button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex gap-4">
                {/* Flow Canvas */}
                <div className="flex-1 border border-border rounded-lg overflow-hidden bg-white" ref={reactFlowWrapper}>
                    <ReactFlow
                        nodes={nodes}
                        edges={edges}
                        onNodesChange={onNodesChange}
                        onEdgesChange={onEdgesChange}
                        onConnect={onConnect}
                        onNodeClick={onNodeClick}
                        onPaneClick={onPaneClick}
                        nodeTypes={nodeTypes}
                        defaultEdgeOptions={defaultEdgeOptions}
                        fitView
                        snapToGrid
                        snapGrid={SNAP_GRID}
                    >
                        <Background gap={20} className="opacity-30" />
                        <Controls />
                        <MiniMap 
                            className="bg-muted" 
                            nodeColor="#3c6e47"
                        />
                    </ReactFlow>
                </div>

                {/* Sidebar */}
                <Card className="w-80 flex-shrink-0">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-sm font-heading">
                            {selectedNode ? 'Proprietà Step' : 'Info Workflow'}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {selectedNode ? (
                            <div className="space-y-4">
                                <div>
                                    <p className="font-medium">{selectedNode.data.effective_config?.name || selectedNode.data.label}</p>
                                    <p className="text-xs text-muted-foreground font-mono">
                                        {selectedNode.data.effective_config?.code || selectedNode.data.code}
                                    </p>
                                    {selectedNode.data.step_library_id && (
                                        <Badge variant="outline" className="mt-2 text-xs">
                                            <Cube className="w-3 h-3 mr-1" />Da Libreria
                                        </Badge>
                                    )}
                                </div>
                                
                                <div className="flex gap-2">
                                    <Button variant="outline" size="sm" className="flex-1" onClick={openNodeEditor}>
                                        <Gear className="w-3 h-3 mr-1" />
                                        Configura
                                    </Button>
                                    <Button variant="outline" size="sm" className="text-destructive" onClick={deleteSelectedNode}>
                                        <Trash className="w-3 h-3" />
                                    </Button>
                                </div>
                                
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Ruolo:</span>
                                        <span>{ROLE_LABELS[selectedNode.data.effective_config?.assigned_role] || '-'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">SLA:</span>
                                        <span>{selectedNode.data.effective_config?.sla_days || 7} giorni</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Bloccante:</span>
                                        <span>{selectedNode.data.effective_config?.is_blocking ? 'Sì' : 'No'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Approvazione:</span>
                                        <span>{selectedNode.data.effective_config?.approval_required ? 'Richiesta' : 'No'}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Documenti:</span>
                                        <span>{selectedNode.data.effective_config?.required_documents?.length || 0}</span>
                                    </div>
                                </div>
                                
                                {Object.keys(selectedNode.data.overrides || {}).length > 0 && (
                                    <div className="p-2 bg-amber-50 border border-amber-200 rounded-md">
                                        <p className="text-xs text-amber-700 font-medium">
                                            Override attivi: {Object.keys(selectedNode.data.overrides).length}
                                        </p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Descrizione</Label>
                                    <Textarea
                                        value={workflowInfo.description}
                                        onChange={(e) => setWorkflowInfo({ ...workflowInfo, description: e.target.value })}
                                        placeholder="Descrizione del workflow..."
                                        rows={3}
                                    />
                                </div>
                                
                                <div className="p-3 bg-muted/30 rounded-md space-y-2 text-sm">
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Step totali:</span>
                                        <span className="font-medium">{nodes.length}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="text-muted-foreground">Connessioni:</span>
                                        <span className="font-medium">{edges.length}</span>
                                    </div>
                                </div>
                                
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                    <p className="text-xs text-blue-700">
                                        <strong>Suggerimento:</strong> Trascina gli step per riposizionarli. 
                                        Collega gli step trascinando dal pallino inferiore a quello superiore.
                                    </p>
                                </div>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Step Library Dialog */}
            <Dialog open={stepLibraryDialogOpen} onOpenChange={setStepLibraryDialogOpen}>
                <DialogContent className="max-w-3xl max-h-[80vh]">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Aggiungi Step da Libreria</DialogTitle>
                    </DialogHeader>
                    <div className="flex gap-3 mb-4">
                        <Input
                            placeholder="Cerca step..."
                            value={stepFilter}
                            onChange={(e) => setStepFilter(e.target.value)}
                            className="flex-1"
                        />
                        <Select value={stepCategoryFilter} onValueChange={setStepCategoryFilter}>
                            <SelectTrigger className="w-48">
                                <SelectValue placeholder="Categoria" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tutte</SelectItem>
                                <SelectItem value="generale">Generale</SelectItem>
                                <SelectItem value="commerciale">Commerciale</SelectItem>
                                <SelectItem value="tecnico">Tecnico</SelectItem>
                                <SelectItem value="amministrativo">Amministrativo</SelectItem>
                                <SelectItem value="installazione">Installazione</SelectItem>
                                <SelectItem value="connessione">Connessione</SelectItem>
                                <SelectItem value="documentale">Documentale</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <ScrollArea className="h-[400px]">
                        <div className="grid grid-cols-2 gap-3 pr-4">
                            {filteredSteps.length === 0 ? (
                                <p className="col-span-2 text-center text-muted-foreground py-8">
                                    Nessuno step trovato. <Link to="/step-library" className="text-primary hover:underline">Crea nuovi step</Link>
                                </p>
                            ) : (
                                filteredSteps.map(step => (
                                    <Card 
                                        key={step.id} 
                                        className="cursor-pointer hover:border-primary transition-colors"
                                        onClick={() => addStepFromLibrary(step)}
                                    >
                                        <CardContent className="p-3">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{step.name}</p>
                                                    <p className="text-xs text-muted-foreground font-mono">{step.code}</p>
                                                </div>
                                                <Plus className="w-4 h-4 text-muted-foreground" />
                                            </div>
                                            <div className="flex flex-wrap gap-1 mt-2">
                                                {step.is_blocking && <Badge variant="outline" className="text-xs py-0">Bloccante</Badge>}
                                                {step.approval_required && <Badge variant="outline" className="text-xs py-0">Approv.</Badge>}
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </div>
                    </ScrollArea>
                </DialogContent>
            </Dialog>

            {/* Node Editor Dialog */}
            <Dialog open={nodeEditorDialogOpen} onOpenChange={setNodeEditorDialogOpen}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle className="font-heading flex items-center gap-2">
                            Configura Step
                            {editingNodeData?.step_library_id && (
                                <Badge variant="outline" className="text-xs">
                                    <Cube className="w-3 h-3 mr-1" />Da Libreria
                                </Badge>
                            )}
                        </DialogTitle>
                    </DialogHeader>
                    <div className="flex-1 overflow-hidden">
                        {editingNodeData && (
                            <Tabs defaultValue="overrides" className="flex flex-col h-full">
                                <TabsList className="mb-4 grid w-full grid-cols-4">
                                    <TabsTrigger value="overrides" className="gap-1">
                                        <Gear className="w-3.5 h-3.5" />
                                        Proprietà
                                    </TabsTrigger>
                                    <TabsTrigger value="documents" className="gap-1">
                                        <FileText className="w-3.5 h-3.5" />
                                        Documenti
                                        {(editingNodeData.overrides?.required_documents?.length > 0 || editingNodeData.overrides?.optional_documents?.length > 0) && (
                                            <Badge className="ml-1 h-4 px-1 text-[10px] bg-blue-500">Override</Badge>
                                        )}
                                    </TabsTrigger>
                                    <TabsTrigger value="form" className="gap-1">
                                        <TextAlignLeft className="w-3.5 h-3.5" />
                                        Form
                                        {editingNodeData.overrides?.form_sections?.length > 0 && (
                                            <Badge className="ml-1 h-4 px-1 text-[10px] bg-blue-500">Override</Badge>
                                        )}
                                    </TabsTrigger>
                                    <TabsTrigger value="base" className="gap-1">
                                        <ListChecks className="w-3.5 h-3.5" />
                                        Base
                                    </TabsTrigger>
                                </TabsList>
                                
                                <ScrollArea className="flex-1 pr-4" style={{maxHeight: 'calc(70vh - 120px)'}}>
                                    {/* Properties Override Tab */}
                                    <TabsContent value="overrides" className="space-y-4 m-0">
                                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                            <p className="text-sm text-blue-700">
                                                <strong>Override Locali:</strong> Le modifiche qui si applicano solo a questo prodotto, 
                                                senza alterare la definizione globale dello step nella libreria.
                                            </p>
                                        </div>
                                        
                                        <div className="space-y-2">
                                            <Label>Nome (override)</Label>
                                            <Input
                                                value={editingNodeData.overrides?.name || ''}
                                                onChange={(e) => updateOverride('name', e.target.value || undefined)}
                                                placeholder={editingNodeData.step_snapshot?.name || 'Nome step'}
                                            />
                                        </div>
                                        
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="space-y-2">
                                                <Label>Ruolo (override)</Label>
                                                <Select 
                                                    value={editingNodeData.overrides?.assigned_role || '_default'} 
                                                    onValueChange={(v) => updateOverride('assigned_role', v === '_default' ? undefined : v)}
                                                >
                                                    <SelectTrigger>
                                                        <SelectValue placeholder={ROLE_LABELS[editingNodeData.step_snapshot?.assigned_role] || "Da libreria"} />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                        <SelectItem value="_default">Da libreria</SelectItem>
                                                        {roles.map(role => (
                                                            <SelectItem key={role.id} value={role.id}>{role.name}</SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                            </div>
                                            <div className="space-y-2">
                                                <Label>SLA (override)</Label>
                                                <Input
                                                    type="number"
                                                    value={editingNodeData.overrides?.sla_days || ''}
                                                    onChange={(e) => updateOverride('sla_days', e.target.value ? parseInt(e.target.value) : undefined)}
                                                    placeholder={String(editingNodeData.step_snapshot?.sla_days || 7)}
                                                />
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-center gap-6 p-3 bg-muted/30 rounded-md">
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    id="override_blocking"
                                                    checked={editingNodeData.overrides?.is_blocking ?? editingNodeData.step_snapshot?.is_blocking}
                                                    onCheckedChange={(checked) => updateOverride('is_blocking', checked)}
                                                />
                                                <Label htmlFor="override_blocking">Bloccante</Label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    id="override_approval"
                                                    checked={editingNodeData.overrides?.approval_required ?? editingNodeData.step_snapshot?.approval_required}
                                                    onCheckedChange={(checked) => updateOverride('approval_required', checked)}
                                                />
                                                <Label htmlFor="override_approval">Approvazione</Label>
                                            </div>
                                        </div>
                                        
                                        {Object.keys(editingNodeData.overrides || {}).filter(k => editingNodeData.overrides[k] !== undefined).length > 0 && (
                                            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm text-amber-700">
                                                        <strong>Override attivi:</strong> {Object.keys(editingNodeData.overrides).filter(k => editingNodeData.overrides[k] !== undefined).length} proprietà modificate
                                                    </p>
                                                    <Button 
                                                        variant="ghost" 
                                                        size="sm" 
                                                        className="text-amber-700 hover:text-amber-900 h-7"
                                                        onClick={() => setEditingNodeData(prev => ({...prev, overrides: {}}))}
                                                    >
                                                        Resetta Override
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </TabsContent>
                                    
                                    {/* Documents Override Tab */}
                                    <TabsContent value="documents" className="space-y-4 m-0">
                                        <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                            <p className="text-sm text-blue-700">
                                                <strong>Documenti Prodotto:</strong> Configura i documenti richiesti per questo step.
                                                Puoi personalizzare i documenti solo per questo prodotto.
                                            </p>
                                        </div>
                                        
                                        {/* Toggle between base and override */}
                                        <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-md">
                                            <div className="flex items-center gap-2">
                                                <Checkbox
                                                    id="use_documents_override"
                                                    checked={editingNodeData.overrides?.required_documents !== undefined || editingNodeData.overrides?.optional_documents !== undefined}
                                                    onCheckedChange={(checked) => {
                                                        if (checked) {
                                                            // Initialize override with base documents
                                                            const baseRequired = editingNodeData.step_snapshot?.required_documents || [];
                                                            const baseOptional = editingNodeData.step_snapshot?.optional_documents || [];
                                                            setEditingNodeData(prev => ({
                                                                ...prev,
                                                                overrides: {
                                                                    ...prev.overrides,
                                                                    required_documents: JSON.parse(JSON.stringify(baseRequired)),
                                                                    optional_documents: JSON.parse(JSON.stringify(baseOptional))
                                                                }
                                                            }));
                                                        } else {
                                                            // Remove override
                                                            setEditingNodeData(prev => {
                                                                const newOverrides = {...prev.overrides};
                                                                delete newOverrides.required_documents;
                                                                delete newOverrides.optional_documents;
                                                                return {...prev, overrides: newOverrides};
                                                            });
                                                        }
                                                    }}
                                                />
                                                <Label htmlFor="use_documents_override" className="text-sm">
                                                    Usa configurazione documenti personalizzata per questo prodotto
                                                </Label>
                                            </div>
                                        </div>

                                        {editingNodeData.overrides?.required_documents !== undefined || editingNodeData.overrides?.optional_documents !== undefined ? (
                                            <div className="border rounded-lg p-4 bg-white">
                                                <StepDocumentBuilder
                                                    requiredDocuments={editingNodeData.overrides?.required_documents || []}
                                                    optionalDocuments={editingNodeData.overrides?.optional_documents || []}
                                                    onChange={(required, optional) => {
                                                        setEditingNodeData(prev => ({
                                                            ...prev,
                                                            overrides: {
                                                                ...prev.overrides,
                                                                required_documents: required,
                                                                optional_documents: optional
                                                            }
                                                        }));
                                                    }}
                                                />
                                            </div>
                                        ) : (
                                            <div className="space-y-4">
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <Cube className="w-4 h-4" />
                                                    <span>Documenti configurati dalla Step Library:</span>
                                                </div>
                                                <div className="border rounded-lg p-4 bg-muted/20">
                                                    <StepDocumentBuilder
                                                        requiredDocuments={editingNodeData.step_snapshot?.required_documents || []}
                                                        optionalDocuments={editingNodeData.step_snapshot?.optional_documents || []}
                                                        readOnly={true}
                                                    />
                                                </div>
                                            </div>
                                        )}
                                    </TabsContent>
                                    
                                    {/* Form Override Tab - NEW */}
                                    <TabsContent value="form" className="m-0">
                                        <div className="space-y-4">
                                            <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
                                                <p className="text-sm text-blue-700">
                                                    <strong>Override Form Prodotto:</strong> Puoi personalizzare le sezioni e i campi del form 
                                                    solo per questo prodotto. La configurazione base rimane nella Step Library.
                                                </p>
                                            </div>
                                            
                                            {/* Toggle between base and override */}
                                            <div className="flex items-center gap-4 p-3 bg-muted/30 rounded-md">
                                                <div className="flex items-center gap-2">
                                                    <Checkbox
                                                        id="use_form_override"
                                                        checked={editingNodeData.overrides?.form_sections !== undefined}
                                                        onCheckedChange={(checked) => {
                                                            if (checked) {
                                                                // Initialize override with base form_sections
                                                                const baseFormSections = editingNodeData.step_snapshot?.form_sections || [];
                                                                updateOverride('form_sections', JSON.parse(JSON.stringify(baseFormSections)));
                                                            } else {
                                                                // Remove override
                                                                updateOverride('form_sections', undefined);
                                                            }
                                                        }}
                                                    />
                                                    <Label htmlFor="use_form_override" className="text-sm">
                                                        Usa configurazione form personalizzata per questo prodotto
                                                    </Label>
                                                </div>
                                            </div>
                                            
                                            {editingNodeData.overrides?.form_sections !== undefined ? (
                                                <div className="border rounded-lg p-4 bg-white">
                                                    <StepFormBuilder
                                                        sections={editingNodeData.overrides?.form_sections || []}
                                                        onChange={(sections) => updateOverride('form_sections', sections)}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                        <Cube className="w-4 h-4" />
                                                        <span>Form configurato dalla Step Library:</span>
                                                    </div>
                                                    {editingNodeData.step_snapshot?.form_sections?.length > 0 ? (
                                                        <div className="border rounded-lg p-4 bg-muted/20">
                                                            <StepFormBuilder
                                                                sections={editingNodeData.step_snapshot?.form_sections || []}
                                                                onChange={() => {}}
                                                                readOnly={true}
                                                            />
                                                        </div>
                                                    ) : (
                                                        <div className="text-center py-8 border-2 border-dashed rounded-lg">
                                                            <TextAlignLeft className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                                                            <p className="text-muted-foreground">
                                                                Nessun form configurato nella Step Library
                                                            </p>
                                                            <p className="text-xs text-muted-foreground mt-1">
                                                                Attiva l'override per creare un form specifico per questo prodotto
                                                            </p>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>
                                    
                                    {/* Base Configuration Tab */}
                                    <TabsContent value="base" className="space-y-4 m-0">
                                        <p className="text-sm text-muted-foreground">
                                            Configurazione originale dallo step in libreria (sola lettura).
                                        </p>
                                        
                                        <div className="space-y-3 p-4 bg-muted/30 rounded-md">
                                            <div className="grid grid-cols-2 gap-2 text-sm">
                                                <div><span className="text-muted-foreground">Nome:</span></div>
                                                <div className="font-medium">{editingNodeData.step_snapshot?.name}</div>
                                                
                                                <div><span className="text-muted-foreground">Codice:</span></div>
                                                <div className="font-mono">{editingNodeData.step_snapshot?.code}</div>
                                                
                                                <div><span className="text-muted-foreground">Ruolo:</span></div>
                                                <div>{ROLE_LABELS[editingNodeData.step_snapshot?.assigned_role] || '-'}</div>
                                                
                                                <div><span className="text-muted-foreground">SLA:</span></div>
                                                <div>{editingNodeData.step_snapshot?.sla_days} giorni</div>
                                                
                                                <div><span className="text-muted-foreground">Bloccante:</span></div>
                                                <div>{editingNodeData.step_snapshot?.is_blocking ? 'Sì' : 'No'}</div>
                                                
                                                <div><span className="text-muted-foreground">Approvazione:</span></div>
                                                <div>{editingNodeData.step_snapshot?.approval_required ? 'Sì' : 'No'}</div>
                                                
                                                <div><span className="text-muted-foreground">Form:</span></div>
                                                <div>{editingNodeData.step_snapshot?.form_sections?.length || 0} sezioni</div>
                                            </div>
                                            
                                            {editingNodeData.step_snapshot?.checklist?.length > 0 && (
                                                <div className="pt-2 border-t border-border">
                                                    <p className="text-sm text-muted-foreground mb-2">Checklist:</p>
                                                    <ul className="text-xs space-y-1">
                                                        {editingNodeData.step_snapshot.checklist.map((item) => (
                                                            <li key={`checklist-${item}`} className="flex items-center gap-1">
                                                                <Check className="w-3 h-3 text-muted-foreground" />
                                                                {item}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                            )}
                                        </div>
                                    </TabsContent>
                                </ScrollArea>
                            </Tabs>
                        )}
                    </div>
                    <DialogFooter className="mt-4 pt-4 border-t">
                        <Button variant="outline" onClick={() => setNodeEditorDialogOpen(false)}>
                            Annulla
                        </Button>
                        <Button onClick={saveNodeChanges} data-testid="save-node-button">
                            Salva Configurazione
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
